import { useDraggable } from "@dnd-kit/core";
import { GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Matching-term chrome:
 * - resting: white fill, secondary outline
 * - dragging / dropped: secondary outline + light secondary fill (quiz “selected”)
 * Drag overlay sits on an opaque underlay so the /10 tint doesn’t look glassy.
 */
export const TERM_CHROME = {
  resting: "border-secondary bg-background text-primary",
  selected: "border-secondary bg-secondary/10 text-primary",
  grip: "text-secondary/60",
} as const;

interface DraggableTermProps {
  term: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Draggable matching term chip — used as the source item in matching quizzes.
 */
const DraggableTerm = ({ term, disabled, className }: DraggableTermProps) => {
  const termId = (value: string) => `term:${value}`;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: termId(term),
    data: { term },
    disabled,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn(
        "flex w-full max-w-full items-center gap-2 rounded-xl border-2 px-4 py-2 shadow-sm transition-shadow hover:shadow-md",
        TERM_CHROME.resting,
        disabled
          ? "cursor-not-allowed opacity-40"
          : "cursor-grab active:cursor-grabbing",
        isDragging && "opacity-0",
        className
      )}
    >
      <GripVertical
        className={cn("h-3.5 w-3.5 shrink-0", TERM_CHROME.grip)}
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1 text-left font-semibold whitespace-normal">
        {term}
      </span>
    </div>
  );
};

export default DraggableTerm;
